<script setup lang="ts">
/**
 * App.vue - Root Application Component
 * 
 * The main application component that provides:
 * - NavBar component (only shown when authenticated)
 * - RouterView for page content
 * - Basic layout structure
 * 
 * Requirements: 12.3, 12.4, 12.5
 */
import { computed, onMounted, ref } from 'vue';
import { RouterView } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import NavBar from '@/components/NavBar.vue';

const authStore = useAuthStore();
const isInitializing = ref(true);

// Only show NavBar when user is authenticated
const showNavBar = computed(() => authStore.isAuthenticated);

// Initialize auth on app mount
onMounted(async () => {
  try {
    await authStore.initialize();
  } finally {
    isInitializing.value = false;
  }
});
</script>

<template>
  <div id="app">
    <!-- Loading state while initializing auth -->
    <div v-if="isInitializing" class="d-flex justify-content-center align-items-center" style="min-height: 100vh;">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">載入中...</span>
      </div>
    </div>
    
    <template v-else>
      <!-- Navigation Bar - Only show when authenticated -->
      <NavBar v-if="showNavBar" />
      
      <!-- Main Content -->
      <main>
        <RouterView />
      </main>
    </template>
  </div>
</template>

<style>
/* Global styles */
#app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

main {
  flex: 1;
  background-color: #f8f9fa;
}

/* Ensure proper spacing when NavBar is present */
#app > main {
  min-height: calc(100vh - 56px);
}

/* Loading spinner animation */
.spinner-border {
  animation: spinner-border 0.75s linear infinite;
}

@keyframes spinner-border {
  to {
    transform: rotate(360deg);
  }
}

/* Custom scrollbar for webkit browsers */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}

/* Utility classes */
.cursor-pointer {
  cursor: pointer;
}

.text-truncate-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
